/**
 * Product Form Component
 * File: assets/product-form.js          (REPLACE)
 *
 * ============================================================
 * CHANGED 27 SEP 2026
 * ============================================================
 *
 * 1. WHAT HAPPENS AFTER ADD TO CART IS NOW A SETTING
 *
 *    This file used to end every successful add with
 *
 *        if (window.CartDrawer) window.CartDrawer.open();
 *
 *    hardcoded. Opening the drawer was the only possible outcome, and
 *    changing it meant editing JavaScript.
 *
 *    It now reads a mode and branches:
 *
 *        drawer    add, then open the cart drawer   (unchanged behaviour)
 *        checkout  add, then go straight to /checkout
 *
 *    Resolution order, first valid value wins:
 *
 *        a) data-after-add on the <form>     per-section override
 *        b) window.GG_AFTER_ADD              the theme setting
 *        c) "drawer"                         if neither is set
 *
 *    (a) is what lets one section differ from the rest. (b) is set once in
 *    layout/theme.liquid from the theme setting, which is why the five
 *    forms that DON'T carry the attribute (the product page snippet and the
 *    three landing pages) pick up the global behaviour without any of those
 *    files being touched. (c) means a theme with neither still works.
 *
 * 2. THE QUANTITY SELECTOR WAS BEING IGNORED — THIS IS A REAL BUG FIX
 *
 *    The call was
 *
 *        await window.CartAPI.addItem(variantId, 1, addItemOptions);
 *
 *    with the quantity hardcoded to 1. Meanwhile sections/homepage-pdp.liquid
 *    renders a working stepper at <input name="quantity"> inside this very
 *    form, and snippets/sticky-atc.liquid keeps a second stepper in sync with
 *    it. A customer could set the quantity to 3, watch the button price
 *    update to 3x, click Add to Cart, and get one bag.
 *
 *    It now reads the field. If there is no quantity field it falls back to
 *    1, which is what every form without a stepper needs anyway.
 *
 * 3. THE CAPTURED BUTTON LABEL IS ACTUALLY USED NOW
 *
 *    originalText was assigned and never read. In checkout mode the label is
 *    swapped while the redirect is in flight, because a button that goes
 *    dead for a second with no explanation gets clicked again.
 */
(function () {
  "use strict";

  var VALID_MODES = ["drawer", "checkout"];

  /**
   * Shopify serves localized storefronts under a path prefix, so a store
   * with markets enabled has its checkout at /en-gb/checkout, not /checkout.
   * Shopify.routes.root is "/" on a single-locale store and "/en-gb/" on a
   * localized one, so this is correct in both cases.
   */
  function checkoutUrl() {
    var root =
      (window.Shopify && window.Shopify.routes && window.Shopify.routes.root) ||
      "/";
    return root + "checkout";
  }

  function resolveMode(form) {
    var perSection = form.getAttribute("data-after-add");
    if (VALID_MODES.indexOf(perSection) !== -1) return perSection;

    var global = window.GG_AFTER_ADD;
    if (VALID_MODES.indexOf(global) !== -1) return global;

    return "drawer";
  }

  /**
   * Read the quantity out of the form. Scoped to the form on purpose: the
   * sticky bar has a second stepper, but its input carries no name attribute
   * and lives outside the form, so it cannot be picked up here by accident.
   * It writes through to this one instead.
   */
  function readQuantity(form) {
    var input = form.querySelector('[name="quantity"]');
    if (!input) return 1;

    var n = parseInt(input.value, 10);
    if (isNaN(n) || n < 1) return 1;
    if (n > 999) return 999;
    return n;
  }

  const ProductForm = {
    init() {
      // Handle product form submissions
      document.querySelectorAll("[data-product-form]").forEach((form) => {
        form.addEventListener("submit", async (e) => {
          e.preventDefault();

          const submitButton = form.querySelector("[data-add-to-cart]");
          if (!submitButton || submitButton.disabled) return;

          const variantSelect = form.querySelector("[data-variant-select]");
          if (!variantSelect) return;

          const variantId = variantSelect.value;
          if (!variantId) return;

          const quantity = readQuantity(form);
          const mode = resolveMode(form);

          const addItemOptions = {};
          const sellingPlanInput = form.querySelector('[name="selling_plan"]');
          if (sellingPlanInput && sellingPlanInput.value) {
            addItemOptions.selling_plan = sellingPlanInput.value;
          }

          const properties = {};
          form.querySelectorAll('[name^="properties["]').forEach((input) => {
            const name = input.getAttribute("name") || "";
            const match = name.match(/^properties\[(.*)\]$/);
            if (!match) return;

            const propertyName = match[1];
            const value = input.value;
            if (propertyName && value !== "") {
              properties[propertyName] = value;
            }
          });

          if (Object.keys(properties).length > 0) {
            addItemOptions.properties = properties;
          }

          // Disable button during submission
          const originalText = submitButton.innerHTML;
          submitButton.disabled = true;
          submitButton.classList.add("opacity-50");

          try {
            await window.CartAPI.addItem(variantId, quantity, addItemOptions);

            if (mode === "checkout") {
              /* Deliberately NOT re-enabling the button. The page is
                 navigating away; putting it back would just give somebody a
                 live button to press twice while the checkout page loads. */
              submitButton.innerHTML =
                '<span class="flex w-full justify-center items-center gap-1">Taking you to checkout</span>';
              window.location.href = checkoutUrl();
              return;
            }

            // Open cart drawer
            if (window.CartDrawer) {
              window.CartDrawer.open();
            }

            // Reset button
            submitButton.innerHTML = originalText;
            submitButton.disabled = false;
            submitButton.classList.remove("opacity-50");
          } catch (error) {
            console.error("Error adding to cart:", error);
            alert(
              "There was an error adding this item to your cart. Please try again."
            );

            // Reset button
            submitButton.innerHTML = originalText;
            submitButton.disabled = false;
            submitButton.classList.remove("opacity-50");
          }
        });
      });
    },
  };

  window.ProductForm = ProductForm;
})();